import React from 'react';
import PropTypes from 'prop-types';
import {
  ClearRefinements,
  CurrentRefinements,
  connectRefinementList,
} from 'react-instantsearch-dom';
import cn from 'classnames';
import { productDownloadNames } from '@helpers/product';
import ToolTip from '@components/ToolTip';
import Button from '@components/Button';
import ButtonClose from '@components/ButtonClose';
import { slugify } from '@helpers/stringFormat';
import RefinementCount from './refinementCount';
import * as styles from './styles.module.scss';
import { DOWNLOAD_OTHER } from '../../../common';

const RefinementList = ({
  items,
  refine,
  createURL,
  attribute,
  operator,
  transformItems,
  helperText,
}) => {
  const refineResults = (e, item) => {
    e.preventDefault();
    refine(item.value);
  };
  const ListWrapper = ({ item, children }) => {
    if (helperText) {
      return (
        <ToolTip
          className={cn('ais-RefinementList-item', {
            'ais-RefinementList-item--selected': item.isRefined,
          })}
          text={helperText[slugify(item.label)]}
          as="li"
        >
          {children}
        </ToolTip>
      );
    }
    return (
      <li
        className={cn('ais-RefinementList-item', {
          'ais-RefinementList-item--selected': item.isRefined,
        })}
      >
        {children}
      </li>
    );
  };
  return (
    <div className="ais-RefinementList">
      <ul className="ais-RefinementList-list">
        {items.map((item, i) => (
          <ListWrapper item={item} key={item.label}>
            <label className="ais-RefinementList-label" htmlFor={item.label}>
              <input
                className="ais-RefinementList-checkbox"
                type="checkbox"
                id={item.label}
                onClick={(e) => refineResults(e, item)}
              />
              <span className="ais-RefinementList-labelText">{item.label}</span>
            </label>
          </ListWrapper>
        ))}
      </ul>
    </div>
  );
};

const CustomRefinementList = connectRefinementList(RefinementList);

const ProductAdvancedFilters = ({ isOpen, toggleMenu, refinements }) => {
  const fileTypeLabel = (items, attribute) => {
    if (attribute === 'fileType') {
      return items.map((item) => {
        return {
          ...item,
          label: productDownloadNames(item.label),
        };
      });
    }
    return items;
  };

  const sortItems = (items, attribute) => {
    const newItems = fileTypeLabel(items, attribute);
    newItems.sort((a, b) => {
      if (a.label > b.label) {
        return 1;
      }
      if (a.label < b.label) {
        return -1;
      }
      return 0;
    });
    const otherFileType = newItems.find(
      (item) => item.label === DOWNLOAD_OTHER,
    );

    if (otherFileType) {
      newItems.splice(newItems.indexOf(otherFileType), 1);
      newItems.push(otherFileType);
    }

    return newItems;
  };

  const outputRefinementList = (listItems) => (
    <>
      {listItems.map((listItem) => (
        <div className={styles.advancedFiltersList} key={listItem.attribute}>
          {listItem.tooltip ? (
            <ToolTip
              as="h2"
              text={listItem.tooltip}
              className={styles.advancedFiltersHeader}
            >
              {listItem.title}
            </ToolTip>
          ) : (
            <h2 className={styles.advancedFiltersHeader}>{listItem.title}</h2>
          )}
          <CustomRefinementList
            attribute={listItem.attribute}
            operator="and"
            transformItems={(items) => sortItems(items, listItem.attribute)}
            helperText={listItem.tooltipAttrs}
          />
        </div>
      ))}
    </>
  );

  return (
    <div
      className={cn(styles.advancedFilters, {
        [styles.isOpen]: isOpen,
      })}
    >
      <div className={`wrapper ${styles.advancedFiltersWrapper}`}>
        <div className={styles.advancedFiltersMeta}>
          <div className="flex justify-end items-center">
            <ClearRefinements
              transformItems={(items) =>
                items.filter((item) => {
                  return (
                    item.attribute !== 'productType.title' &&
                    item.attribute !== 'productFinish.finish.code' &&
                    item.attribute !== 'collection.title.title'
                  );
                })
              }
              translations={{
                reset: 'Clear All',
              }}
            />
            <CurrentRefinements
              transformItems={(items) => {
                const filteredItems = items.filter((item) => {
                  return (
                    item.attribute !== 'productType.title' &&
                    item.attribute !== 'productFinish.finish.code' &&
                    item.attribute !== 'collection.title.title'
                  );
                });
                const transformedItemLabels = filteredItems.map((item) => {
                  if (item.attribute === 'fileType') {
                    item.items.forEach(
                      (el, i) =>
                        (el.label = productDownloadNames(
                          item.currentRefinement[i],
                        )),
                    );
                  }
                  return item;
                });
                return transformedItemLabels;
              }}
            />
            <Button
              className={cn(styles.advancedFiltersButton, 'btn btn-secondary')}
              icon="advanced-l"
              onClick={toggleMenu}
              aria-label="Advanced Filters"
            >
              Advanced Filters
              <RefinementCount />
            </Button>
          </div>
        </div>
        <div className={styles.advancedFiltersMenu}>
          <div>
            <ButtonClose
              screenReader="Close advanced filters"
              white
              onClick={toggleMenu}
              aria-label="Close advanced filters"
            />
          </div>
          <div className={styles.advancedFiltersInner}>
            {outputRefinementList(refinements)}
          </div>
        </div>
      </div>
    </div>
  );
};

ProductAdvancedFilters.propTypes = {
  isOpen: PropTypes.bool,
  toggleMenu: PropTypes.func.isRequired,
  refinements: PropTypes.array.isRequired,
};

ProductAdvancedFilters.defaultProps = {
  isOpen: false,
};

export default ProductAdvancedFilters;
